import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import StockReport from "./StockReport";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//functions
import {
  _t,
  getCookie,
  formatPrice,
  currencySymbolLeft,
  currencySymbolRight,
  checkPermission
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import Moment from "react-moment";
import { MdDelete } from 'react-icons/md';

//context consumer
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { UserContext } from "../../../../../contexts/User";
import KdsModal from "../../kitchen/KdsModal";
import CsvImportExport from "../../../../../components/CsvImportExport";
import DataTable from "../../../../common/DataTable";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";

const FGPurchaseList = () => {
  const { t } = useTranslation();
  //getting context values here

  let {
    branchForSearch,
  } = useContext(RestaurantContext);

  let { authUserInfo, getSupplier, supplierForSearch } = useContext(UserContext);

  // Local states
  const [loading, setLoading] = useState(false);
  const [dataPaginating, setDataPaginating] = useState(false);
  const [fgPurchaseList, setFgPurchaseList] = useState(null);
  const [fgPurchaseListForSearch, setFgPurchaseListForSearch] = useState(null);
  // States hook here
  const [itemData, setItemData] = useState({
    modal: false,
    url: "",
    name: "",
    token: ""
  });
  //search result
  let [searchedFgPurchase, setSearchedFgPurchase] = useState({
    list: null,
    searched: false,
  });

  const [btnDisable, setBtnDisable] = useState("")

  const searchInputRef = useRef(null);

  // image pop up
  const handleOpenPopup = (item) => {
    setItemData({
      modal: true,
      url: BASE_URL + item?.fg_document,
      token: `FG Purchase Document: ${item.invoice_number}`,
    })
  };

  const handleClosePopup = () => {
    setItemData({
      modal: false,
      url: "",
      name: "",
      token: "",
    })
  };

  // Fetch FG Purchase List
  const getFgPurchaseList = async (pageNo) => {
    setLoading(true);
    const url = BASE_URL + "/settings/finished-good-purchase?page=" + pageNo;
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      setFgPurchaseList(res.data[0]);
      setFgPurchaseListForSearch(res.data[1]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error==>", error);
    }
  };

  //useEffect == componentDidMount
  useEffect(() => {
    getFgPurchaseList(1);
    if (!supplierForSearch) {
        getSupplier();
    }
  }, []);

  //search fg purchase here
  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedFgPurchase({ ...searchedFgPurchase, searched: false });
    } else {
      const searchedList = fgPurchaseListForSearch.filter((item) => {
        //name
        let lowerCaseItemSupplierName = item.supplier_name && item.supplier_name.toLowerCase();
        //invoice
        let lowerCaseItemInvoice = item.invoice_number !== null && item.invoice_number.toLowerCase();
        let lowerCaseBranch = item.branch?.name && item.branch.name.toLowerCase();

        return (
          (lowerCaseItemSupplierName && lowerCaseItemSupplierName.includes(searchInput)) ||
          (lowerCaseItemInvoice && lowerCaseItemInvoice.includes(searchInput)) ||
          (lowerCaseBranch && lowerCaseBranch.includes(searchInput))
        );
      });
      setSearchedFgPurchase({
        ...searchedFgPurchase,
        list: searchedList,
        searched: true,
      });
    }
  };

  const onClearSearch = () => {
    setSearchedFgPurchase({ ...searchedFgPurchase, searched: false });
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  //delete confirmation modal
  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleDeleteFgPurchase(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  //delete fg purchase here
  const handleDeleteFgPurchase = (id) => {
    setLoading(true);
    const url = BASE_URL + `/settings/finished-good-purchase/${id}`;
    return axios
      .delete(url, {
        headers: { Authorization: `Bearer ${getCookie()} ` },
      })
      .then((res) => {
        setFgPurchaseList(res.data[0]);
        setFgPurchaseListForSearch(res.data[1]);
        setSearchedFgPurchase({
          ...searchedFgPurchase,
          list: res.data[1],
        });
        setLoading(false);
        ToastSuccess({ title: `${_t(t("FG Purchase has been deleted successfully"))} ` });
      })
      .catch(() => {
        setLoading(false);
        ToastError({ title: `${_t(t("Please try again"))} ` });
      });
  };

  //print bills states and function
  const [newRow, setNewRow] = useState([]);
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  let [newItem, setNewItem] = useState({
    branch: null,
    customer: null,
    invoice: "",
    description: "",
    paymentType: "",
  });

  const componentRef = useRef();

  const handleOnlyPrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: "@page { margin: 15px }"
  });

  const handleSetCsvResponse = useCallback((data) => {
    setFgPurchaseList(data[0]);
    setFgPurchaseListForSearch(data[1]);
    setSearchedFgPurchase({
      ...searchedFgPurchase,
      list: data[1],
    });
  }, [])


  const handleGetPrintData = async (id) => {
    const url =
      BASE_URL +
      "/settings/get-fg-purchase-items/" +
      parseInt(id);
    try {
      const res = await axios
        .get(url, {
          headers: { Authorization: `Bearer ${getCookie()} ` },
        });
        console.log("res.data==>",res.data);
        if (res.data) {
            let purchaseDate = new Date(res.data[1].purchase_date);
            setPurchaseDate(purchaseDate);
            let branch = branchForSearch?.find((item) => item.id === res.data[1].branch_id)
            let supplier = supplierForSearch?.find((item) => item.id === res.data[1].supplier_id);
            setNewItem({
              ...newItem,
              branch,
              description: res.data[1].desc,
              paymentType: res.data[1].payment_type,
              supplier,
              invoice: res.data[1].invoice_number,
              ref_doc: res.data[1].ref_doc_identity,
              transportation: res.data[1].transportation,
              advance_tax: res.data[1].total_advance_tax,
              net_amount: res.data[1].net_amount,
              total_amount: res.data[1].total_amount,
              total_discount: res.data[1].total_discount,
              total_tax: res.data[1].total_tax,
              total_bill: res.data[1].total_bill,
            })
            if (res.data[0] && res.data[0].length > 0) {
              setNewRow([...res.data[0]])
            }
          }
      handleOnlyPrint();

    } catch (error) {
      console.log("error==>", error)
    }
  }

  const handleGetAmount = (index) => {
    let qty = 0;
    let rate = 0;
    if (newRow !== null && newRow[index].qty !== "") {
      qty = parseFloat(newRow[index].qty);
    }
    if (newRow !== null && newRow[index].rate !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    return rate * qty;
  };

  //post button function
  const handlePostApi = async (item) => {
    setBtnDisable(item.id)
    const url = BASE_URL + "/settings/mark-is-post-fgpurchase"
    let formData = new FormData();
    formData.append('id', item.id)
    formData.append('is_post', item.is_post ? 0 : 1)
    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()} ` },
      });

      if (res.data) {
        handleSetCsvResponse(res.data);
        setBtnDisable("")
        ToastSuccess({ title: _t(t(item?.is_post ? "FG Purchase has been unpost" : "FG Purchase has been post")) })
      }

      setDataPaginating(false);
    } catch (err) {

      setDataPaginating(false);
      setBtnDisable("")
      if (err.response?.data?.status === "error") {
        ToastError({ title: _t(t(err.response.data.message || "Please try again")) })
        return
      }
      ToastError({ title: _t(t("Please try again")) })

    }
  }

  //unpost confirmation modal
  const handleUnPostConfirmation = (item) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body px-5">
            <h3>{_t(t("Are you sure?"))}</h3>
            <p className="text-center">{_t(t("You want to un post this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handlePostApi(item);
                  onClose();
                }}
              >
                {_t(t("Yes, unpost it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const csvData = [
    {
      key: "id",
      index: 0,
    },
    {
      key: "branch_name",
      index: 1,
    },
    {
      key: "customer_name",
      index: 2,
    },
    {
      key: "ref_no",
      index: 3,
    },
    {
      key: "manual_invoice_no",
      index: 4,
    },
    {
      key: "payment_type",
      index: 5,
    },
    {
      key: "credit_limit_days",
      index: 6,
    },
    {
      key: "remarks",
      index: 7,
    },
    {
      key: "item_name",
      index: 8,
    },
    {
      key: "qty",
      index: 9,
    },
    {
      key: "unit",
      index: 10,
    },
    {
      key: "rate",
      index: 11,
    },
    {
      key: "discount_percent",
      index: 12,
    },
    {
      key: "tax_percent",
      index: 13,
    }
  ]

  const getListData = useCallback(() => {
    if (searchedFgPurchase.searched) {
      return searchedFgPurchase.list;
    }
    return fgPurchaseList?.data || [];
  }, [searchedFgPurchase, fgPurchaseList]);

  const [multiFg, setMultiFg] = useState({
    fgItems: null,
    is_post: 1,
  });

  const toggleSelectAll = (e) =>{
    e.stopPropagation();
    let updated = {...multiFg.fgItems};
    const listData = (searchedFgPurchase?.searched && searchedFgPurchase?.list) || fgPurchaseList?.data || [];
    if(e.target.checked){
      listData.forEach((item)=>{
        if(!item.is_post){
          updated[item.id] = item.id;
        }
      })
    }else{
      listData.forEach((item)=>{
        if(updated[item.id]){
          delete updated[item.id];
        }
      })
    }

    setMultiFg((curr)=>({
      ...curr,
      fgItems: updated
    }));
  }
  
  const toggleSelect = (id) => {
    let updated = {...multiFg.fgItems};
    if (updated[id]) {
      delete updated[id];
    } else {
      updated[id] = id;
    }
    setMultiFg((curr)=>({
      ...curr,
      fgItems: updated
    }));
  };

  //multi post
  const handleSaveMultiFg = async (e) => {

    e.preventDefault();

    if (!multiFg?.fgItems || !Object.values(multiFg?.fgItems)?.length) {
      ToastError({ title: `${_t(t("Please select item to post"))}` });
      return false;
    }

    setDataPaginating(true);

    const url = BASE_URL + "/settings/fg-purchase-multi-post";
    const formData = {
        fgPurchaseIds: multiFg.fgItems
        ? Object.values(multiFg.fgItems)
        : [],
      is_post: multiFg.is_post ?? 1,
    };
    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setDataPaginating(false)
        setFgPurchaseList(res.data[0])
        setFgPurchaseListForSearch(res.data[1]);
        setSearchedFgPurchase({
          ...searchedFgPurchase,
          list: res.data[1],
        });
        setMultiFg({
          fgItems: null,
          is_post: 1,
        });
        ToastSuccess({ title: `${_t(t("FG Purchase has been posted"))}` });
        if(document.getElementById("multi_fg_check")){
          document.getElementById("multi_fg_check").checked = false;
        }
      }
    } catch (error) {
      setDataPaginating(false);
      if (error?.response?.data && error.response.data?.status == 0) {
        ToastError({ title: `${_t(t(error.response.data.message))}` });
        return false;
      }
      ToastError({ title: `${_t(t("Please try again"))}` });
    }
  };

  const checkFgPost = useMemo(()=>{
    const permissions = authUserInfo?.permissions || [];
    return checkPermission(permissions, "FG PI Post");
  },[authUserInfo?.permissions])

  const COLUMNS = [
    {
      label: "S/L",
      render: (item, index) => {
        if (searchedFgPurchase.searched) {
          return index + 1;
        }
        return index + 1 + ((fgPurchaseList?.current_page || 1) - 1) * (fgPurchaseList?.per_page || 0);
      }
    },
    {
      labelRender:()=>{
        return (
          <div className="d-flex justify-content-center align-items-center">
            <button disabled={!checkFgPost} className="btn btn-sm btn-dark mr-2" onClick={(e)=>handleSaveMultiFg(e)}>
              <i className="fa fa-check-square-o "></i>
            </button>
            <input
              id="multi_fg_check"
              type="checkbox"
              className="form-check-input table-text"
              onChange={(e) => {
                toggleSelectAll(e);
              }}
            />
          </div>
        )
      },
      render:(item)=>{
        return (
          <input
            disabled={item.is_post}
            id={item.id}
            type="checkbox"
            className="form-check-input table-text"
            checked={multiFg?.fgItems && (multiFg.fgItems[item.id] ?? 0)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              toggleSelect(item.id)
            }}
          />
        )
      },
      minWidth: "5.5rem"
    },
    {
      label: "Supplier",
      key: "supplier_name",
    },
    {
      label: "Purchase No",
      key: "invoice_number",
    },
    {
      label: "Purchase Date",
      render: (item) => (
        <Moment format="DD-MMMM-YYYY">
          {item.purchase_date}
        </Moment>
      )
    },
    {
      label: "Branch",
      render: (item) => item.branch?.name
    },
    {
      label: "Total",
      render: (item) => (
        <>
          {currencySymbolLeft()}
          {formatPrice(item.total_bill)}
          {currencySymbolRight()}
        </>
      )
    },
    {
      label: "Action",
      render: (item) => (
        <div className="d-flex justify-content-center">

          {item.is_post !== 1 && (
            <NavLink
              title="Edit"
              disabled={item.id === btnDisable}
              className="btn btn-primary ml-2 rounded-sm del-btn"
              to={`/dashboard/manage/stock/fg-invoice-edit/${item.id}`}
              style={{ height: "1.7rem", width: "1.7rem" }}
            >
              <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
            </NavLink>
          )}

          <NavLink
            title="duplicate fg purchase"
            to={`/dashboard/manage/stock/fg-invoice-edit/${item.id}?duplicate=1`}
            className="btn btn-success ml-2 rounded-sm del-btn"
          >
            <i className="fa fa-clone"></i>
          </NavLink>

          {item.is_post !== 1 && (
            <button
              disabled={item.id === btnDisable}
              className="btn btn-danger ml-2 rounded-sm del-btn"
              onClick={() => handleDeleteConfirmation(item.id)}
            >
              <MdDelete className="del-icon" />
            </button>
          )}

          <button
            className="btn btn-secondary ml-2 rounded-sm del-btn"
            onClick={() => handleGetPrintData(item.id)}
          >
            <i className="fa fa-print edit-icon"></i>
          </button>

          {(checkFgPost && item.is_post !== 1 ? <button
            className="btn btn-dark ml-2 rounded-sm del-btn"
            title="Post FG"
            style={{ padding: "1px 0px 0px 1px" }}
            disabled={loading || item.id === btnDisable}
            onClick={() => handlePostApi(item)}
          >
            <i className="fa fa-check-square-o" aria-hidden="true"></i>
          </button> : null)
          }

          {(checkFgPost && authUserInfo?.details?.user_type === "superAdmin" && item.is_post === 1 ? <button
            className="btn btn-warning ml-2 rounded-sm del-btn"
            title="Unpost FG"
            style={{ padding: "1px 0px 0px 1px" }}
            disabled={loading || item.id === btnDisable}
            onClick={() => handleUnPostConfirmation(item)}
          >
            <i className="fa fa-check-square-o" aria-hidden="true"></i>
          </button> : null)
          }

          {item?.fg_document &&
            <button
              title="FG document"
              className="btn btn-light ml-2 rounded-sm del-btn"
              onClick={() => handleOpenPopup(item)}
            >
              <i className="fa fa-eye"></i>
            </button>
          }

        </div>
      )
    },
    {
      label: "Created Date",
      render: (item) => (
        <Moment format="DD-MMMM-YYYY hh:mm A">
          {item?.created_at}
        </Moment>
      )
    },
  ]

  return (
    <>
      <Helmet>
        <title>{_t(t("Finished Goods Purchase"))}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div key="fragment3">

                <div className="t-bg-white">
                  <div className=" row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                    <div className="col-md-7 col-lg-6 t-mb-15 mb-md-0 d-flex justify-content-between align-items-center">
                      <ul className="t-list fk-breadcrumb">
                        <li className="fk-breadcrumb__list">
                          <span className="t-link fk-breadcrumb__link text-capitalize">
                            {t("Finished Goods Purchase")}
                          </span>
                        </li>
                      </ul>
                      {/* <CsvImportExport
                        filename="FG Purchase Sample Format CSV"
                        csvLength={14}
                        exportUrl="/settings/export-fg-invoice"
                        importUrl="/settings/upload-fg-invoice"
                        exportBtnName="Sample Format CSV"
                        csvData={csvData}
                        onCsvResponse={handleSetCsvResponse}
                      /> */}
                    </div>
                    <div className="col-md-5 col-lg-6">
                      <div className="row gx-3 align-items-center">
                        {/* Search customer */}
                        <div className="col-md-9 t-mb-15 mb-md-0">
                          <div className="input-group">
                            <div className="form-file">
                              <input
                                type="text"
                                className="form-control border-0 form-control--light-1 rounded-left"
                                placeholder={_t(t("Search")) + ".."}
                                onChange={handleSearch}
                                ref={searchInputRef}
                              />
                            </div>
                            <button
                              className="btn btn-primary rounded-right"
                              type="button"
                            >
                              <i
                                className="fa fa-search"
                                aria-hidden="true"
                              ></i>
                            </button>
                          </div>
                        </div>

                        {/* Add customer modal trigger button */}
                        <div className="col-md-3 text-md-right">
                          <NavLink
                            to="/dashboard/manage/stock/fg-invoice-add"
                            className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                          >
                            {_t(t("add new"))}
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <DataTable
                  loading={loading || dataPaginating}
                  data={getListData()}
                  columns={COLUMNS}
                  emptyText={_t(t("No data available"))}
                  pagination={{
                    isPageLoading: loading,
                    isPagination: true,
                    pageCount: fgPurchaseList?.last_page,
                    currentPage: fgPurchaseList?.current_page,
                    from: fgPurchaseList?.from,
                    to: fgPurchaseList?.to,
                    total: fgPurchaseList?.total,
                  }}
                  handleFetchData={getFgPurchaseList}
                  searchPayload={{
                    isSearched: searchedFgPurchase.searched,
                    onClearSearch,
                  }}
                />

              </div>

            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>

      <KdsModal
        show={itemData.modal}
        handleClose={handleClosePopup}
        itemData={itemData}
      />

      {/* main body ends */}
      <StockReport
        name="FG Purchase"
        purchaseDate={purchaseDate}
        newItem={newItem}
        newRow={newRow}
        invoiceNo={"Purchase No"}
        inoviceDate={"Purchase Date"}
        handleGetTotal={""}
        handleAmount={""}
        handleTaxAmount={""}
        handleDisAmount={""}
        handleGetSubTotal={""}
        handleGetAmount={handleGetAmount}
        stockPrint={componentRef}
      />
      {/* <ViewAccountSheet
        accountLedger={ledgerData}
        setAccountLedger={setLedgerData}
      /> */}
    </>
  );
};

export default FGPurchaseList;
